import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Music, Users, Calendar, Film, Sparkles, Building2, Mic2, Globe, Briefcase, PartyPopper } from 'lucide-react';
import { Layout } from '../components/layout/Layout';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function ServicesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const mainServices = [
    {
      id: 'booking',
      slug: 'booking-artistico',
      icon: Music,
      color: '#D4AF37',
      name: { es: 'Booking Artístico', en: 'Artistic Booking' },
      short: { es: 'Representación y contratación de talento', en: 'Talent representation and hiring' },
      description: {
        es: 'Representación y contratación de DJs nacionales e internacionales, bandas y proyectos musicales, artistas para eventos privados y corporativos.',
        en: 'Representation and hiring of national and international DJs, bands and musical projects, artists for private and corporate events.'
      },
      features: [
        { es: 'Curación artística según concepto del evento', en: 'Artistic curation according to event concept' },
        { es: 'Negociación de fees y contratos', en: 'Fee negotiation and contracts' },
        { es: 'Coordinación técnica y logística', en: 'Technical and logistics coordination' },
        { es: 'Riders técnicos y hospitalidad', en: 'Technical riders and hospitality' },
      ],
      image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'management',
      slug: 'management-artistico',
      icon: Users,
      color: '#C0C0C0',
      name: { es: 'Management Artístico', en: 'Artist Management' },
      short: { es: 'Gestión integral de carrera artística', en: 'Comprehensive career management' },
      description: {
        es: 'Gestión completa de carrera para artistas: estrategia de marca personal, posicionamiento internacional, planificación de lanzamientos y giras.',
        en: 'Complete career management for artists: personal brand strategy, international positioning, release and tour planning.'
      },
      features: [
        { es: 'Estrategia de marca personal', en: 'Personal brand strategy' },
        { es: 'Posicionamiento y crecimiento internacional', en: 'International positioning and growth' },
        { es: 'Planificación de lanzamientos y tours', en: 'Release and tour planning' },
        { es: 'Desarrollo de imagen y narrativa artística', en: 'Image and artistic narrative development' },
      ],
      image: 'https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'events',
      slug: 'produccion-eventos',
      icon: Calendar,
      color: '#D4AF37',
      name: { es: 'Producción de Eventos', en: 'Event Production' },
      short: { es: 'Diseño y ejecución de experiencias', en: 'Design and execution of experiences' },
      description: {
        es: 'Diseño y ejecución de experiencias musicales únicas: eventos boutique y VIP, festivales culturales, activaciones de marca, eventos temáticos.',
        en: 'Design and execution of unique musical experiences: boutique and VIP events, cultural festivals, brand activations, themed events.'
      },
      features: [
        { es: 'Concepto creativo y diseño de experiencia', en: 'Creative concept and experience design' },
        { es: 'Producción técnica completa', en: 'Complete technical production' },
        { es: 'Coordinación de staff, talento y proveedores', en: 'Staff, talent and supplier coordination' },
        { es: 'Eventos rooftop, beach, sunset, carnaval', en: 'Rooftop, beach, sunset, carnival events' },
      ],
      image: 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'audiovisual',
      slug: 'produccion-audiovisual',
      icon: Film,
      color: '#C0C0C0',
      name: { es: 'Producción Audiovisual', en: 'Audiovisual Production' },
      short: { es: 'Contenido para crecimiento digital', en: 'Content for digital growth' },
      description: {
        es: 'Grabación de shows en vivo, producción de contenido para YouTube y redes sociales, reels, aftermovies y material promocional profesional.',
        en: 'Live show recording, content production for YouTube and social media, reels, aftermovies and professional promotional material.'
      },
      features: [
        { es: 'Grabación de shows en vivo', en: 'Live show recording' },
        { es: 'Contenido para YouTube y redes', en: 'YouTube and social media content' },
        { es: 'Reels, aftermovies, teasers', en: 'Reels, aftermovies, teasers' },
        { es: 'Material promocional y press kits', en: 'Promotional material and press kits' },
      ],
      image: 'https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'branding',
      slug: 'branding-marketing',
      icon: Sparkles,
      color: '#D4AF37',
      name: { es: 'Branding & Marketing', en: 'Branding & Marketing' },
      short: { es: 'Arte con visión de negocio', en: 'Art with business vision' },
      description: {
        es: 'Desarrollo de identidad visual, diseño de flyers y campañas, estrategias de lanzamiento y gestión de colaboraciones estratégicas.',
        en: 'Visual identity development, flyer and campaign design, launch strategies and strategic collaboration management.'
      },
      features: [
        { es: 'Desarrollo de identidad visual', en: 'Visual identity development' },
        { es: 'Diseño de flyers y campañas', en: 'Flyer and campaign design' },
        { es: 'Estrategias de lanzamiento', en: 'Launch strategies' },
        { es: 'Gestión de colaboraciones y partnerships', en: 'Collaboration and partnership management' },
      ],
      image: 'https://images.pexels.com/photos/1587927/pexels-photo-1587927.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'experiences',
      slug: 'experiencias-exclusivas',
      icon: Building2,
      color: '#C0C0C0',
      name: { es: 'Experiencias Exclusivas', en: 'Exclusive Experiences' },
      short: { es: 'Eventos premium y comunidad', en: 'Premium events and community' },
      description: {
        es: 'Eventos de aforo limitado, experiencias premium para marcas y clientes selectos, creación de comunidades culturales Brasil-México.',
        en: 'Limited capacity events, premium experiences for brands and select clients, creation of Brazil-Mexico cultural communities.'
      },
      features: [
        { es: 'Eventos de aforo limitado y exclusivo', en: 'Limited and exclusive capacity events' },
        { es: 'Experiencias premium para marcas', en: 'Premium brand experiences' },
        { es: 'Creación de comunidades culturales', en: 'Cultural community creation' },
        { es: 'Conexión Brasil-México', en: 'Brazil-Mexico connection' },
      ],
      image: 'https://images.pexels.com/photos/260689/pexels-photo-260689.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
  ];

  const additionalServices = [
    { icon: Mic2, name: { es: 'Producción Musical', en: 'Music Production' } },
    { icon: Building2, name: { es: 'Renta de Venues', en: 'Venue Rental' } },
    { icon: Globe, name: { es: 'Coordinación Legal y Contratos', en: 'Legal & Contract Coordination' } },
    { icon: Briefcase, name: { es: 'Logística y Coordinación Técnica', en: 'Logistics & Technical Coordination' } },
    { icon: PartyPopper, name: { es: 'Shows Personalizados', en: 'Custom Shows' } },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-32 pb-20" data-testid="services-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <img 
                src="/gs-agency-logo-gold.png" 
                alt="GS Agency" 
                className="h-48 md:h-64 w-auto object-contain"
              />
            </motion.div>
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6">
              {t('services.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-8">
              {t('services.title')}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-white/60 text-xl leading-relaxed">
              Soluciones integrales para el entretenimiento. Desde booking artístico hasta producción de eventos completa.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-subtle py-24" data-testid="main-services">
        <div className="container-premium">
          <div className="space-y-24">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={stagger}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <motion.div 
                  variants={fadeInUp}
                  className={index % 2 === 1 ? 'lg:order-2' : ''}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.name[lang]}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                </motion.div>

                <motion.div 
                  variants={fadeInUp}
                  className={index % 2 === 1 ? 'lg:order-1' : ''}
                >
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${service.color}15` }}
                  >
                    <service.icon size={32} style={{ color: service.color }} />
                  </div>
                  <h2 className="font-display text-4xl text-white mb-4">
                    {service.name[lang]}
                  </h2>
                  <p className="text-white/60 text-lg leading-relaxed mb-8">
                    {service.description[lang]}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div 
                          className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                          style={{ backgroundColor: service.color }}
                        />
                        <span className="text-white/70">{feature[lang]}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-3">
                    <Link 
                      to={`/services/${service.slug}`}
                      className="btn-gold inline-flex items-center gap-2"
                      data-testid={`view-${service.id}`}
                    >
                      Ver Detalles
                      <ArrowRight size={18} />
                    </Link>
                    <Link 
                      to="/contact" 
                      className="btn-secondary inline-flex items-center gap-2"
                      data-testid={`contact-${service.id}`}
                    >
                      Solicitar
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-dark py-24" data-testid="additional-services">
        <div className="container-premium">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl text-white">
                Servicios Adicionales
              </h2>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              {additionalServices.map((service, index) => (
                <div
                  key={index}
                  className="premium-card px-6 py-4 flex items-center gap-3"
                >
                  <service.icon size={20} className="text-[#D4AF37]" />
                  <span className="text-white/80">{service.name[lang]}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 section-subtle" data-testid="services-cta">
        <div className="container-premium text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl text-white mb-6">
              ¿Tienes un proyecto en mente?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
              Cuéntanos tu visión y diseñemos juntos una experiencia inolvidable.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
                Empezar Proyecto
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
